// EditorLayout.tsx - Presentational component for main editor UI in PDFEditor
import React, { useMemo, useEffect, useRef } from 'react';
import EditorNavigation from './components/EditorNavigation';
import CoverPageEditor from './components/editors/CoverPageEditor';
import DocumentReferenceEditor from './components/editors/DocumentReferenceEditor';
import ExecutiveSummaryEditor from './components/editors/ExecutiveSummaryEditor';
import DisclaimerEditor from './components/editors/DisclaimerEditor';
import MethodologyEditor from './components/editors/MethodologyEditor';
import RecommendationsEditor from './components/editors/RecommendationsEditor';
import ConclusionEditor from './components/editors/ConclusionEditor';
import { DetailedFinding } from './types/report.types';
import FindingsEditor from './components/editors/FindingsEditor';
import TargetDetailsEditor from './components/editors/TargetDetailsEditor';
import ProjectObjectivesEditor from './components/editors/ProjectObjectivesEditor';
import ScopeEditor from './components/editors/ScopeEditor';
import FindingsSummaryEditor from './components/editors/FindingsSummaryEditor';
import VulnerabilityRatingsEditor from './components/editors/VulnerabilityRatingsEditor';
import CriticalFindingsEditor from './components/editors/CriticalFindingsEditor';
import HighFindingsEditor from './components/editors/HighFindingsEditor';
import MediumFindingsEditor from './components/editors/MediumFindingsEditor';
import LowFindingsEditor from './components/editors/LowFindingsEditor';
import KeyFindingsEditor from './components/editors/KeyFindingsEditor';
import PreviewSection from './components/PreviewSection';
import ReportTemplate from './components/ReportTemplate';
import ChangeLogSidebar from './components/ChangeLogSidebar';
import SectionRenderer from './SectionRenderer';
import { isAttestationEnabled } from './utils/attestationUtils';
import { ReportData } from './types/report.types';

interface EditorLayoutProps {
  currentData: any;
  reportData: any;
  editorWidth: string;
  dragging: React.MutableRefObject<boolean>;
  setEditorWidth: (width: string) => void;
  activeSection: string;
  setActiveSection: (section: string) => void;
  openChangeLog: () => void;
  handleSave: () => void;
  saveLoading: boolean;
  approveLoading: boolean;
  rejectLoading: boolean;
  canApprove: boolean;
  handleApprove: () => void;
  canReject: boolean;
  handleReject: () => void;
  reportDataStatus: string | undefined;
  handleInputChange: (field: keyof ReportData, value: string) => void;
  handleHtmlChange: (field: keyof ReportData, value: string) => void;
  handleDataChange: (field: string, value: any) => void;
  handleFindingChange: (index: number, field: keyof DetailedFinding, value: string) => void;
  handleRemoveFinding: (index: number) => void;
  handleAddFinding: (severity: string) => void;
  handleTargetDetailsChange: (index: number, field: string, value: any) => void;
  handleAddTarget: () => void;
  handleRemoveTarget: (index: number) => void;
  handleTableChange: (fieldPath: string, value: number) => void;
  handleKeyFindingChange: (index: number, field: string, value: string) => void;
  handleAddKeyFinding: () => void;
  handleRemoveKeyFinding: (index: number) => void;
  handleRecommendationsChange: (recommendations: { title: string; description: string }[]) => void;
  saveSectionChanges: () => void;
  previewRef: React.RefObject<HTMLDivElement>;
  openFullView: () => void;
  previewMode: string;
  setPreviewMode: (mode: string) => void;
  previewHtml: string;
  isFullViewOpen: boolean;
  closeFullView: () => void;
  notification: any;
  hideNotification: () => void;
  isChangeLogOpen: boolean;
  closeChangeLog: () => void;
  changeLog: any[];
  changeLogLoading: boolean;
  previewLoading: boolean;
  sectionData: any;
  // New optional props for Vanta upload
  showUploadToVanta?: boolean;
  onUploadToVanta?: () => void;
  vantaUploadLoading?: boolean;
  vantaUploadProgress?: number;
  onDownloadLOA?: () => void;
  loaDownloading?: boolean;
  userRole?: string;
  children?: React.ReactNode;
}

const EditorLayout: React.FC<EditorLayoutProps> = (props) => {
  // Destructure all props for clarity
  const {
    currentData,
    reportData,
    editorWidth,
    dragging,
    setEditorWidth,
    activeSection,
    setActiveSection,
    openChangeLog,
    handleSave,
    saveLoading,
    approveLoading,
    rejectLoading,
    canApprove,
    handleApprove,
    canReject,
    handleReject,
    reportDataStatus,
    handleInputChange,
    handleHtmlChange,
    handleDataChange,
    handleFindingChange,
    handleRemoveFinding,
    handleAddFinding,
    handleTargetDetailsChange,
    handleAddTarget,
    handleRemoveTarget,
    handleTableChange,
    handleKeyFindingChange,
    handleAddKeyFinding,
    handleRemoveKeyFinding,
    handleRecommendationsChange,
    saveSectionChanges,
    previewRef,
    openFullView,
    previewMode,
    setPreviewMode,
    previewHtml,
    isFullViewOpen,
    closeFullView,
    notification,
    hideNotification,
    isChangeLogOpen,
    closeChangeLog,
    changeLog,
    changeLogLoading,
    previewLoading,
    sectionData,
    userRole,
    children
  } = props;

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [activeSection]);

  const mergedData = useMemo(() => {
    return { ...currentData, ...sectionData };
  }, [currentData, sectionData]);

  const isLoaEnabled = useMemo(() => isAttestationEnabled(mergedData), [mergedData]);

  const canEditTemplate = useMemo(() => {
    const role = userRole?.toUpperCase();
    const isLocked = mergedData?.finding_config?.is_template_locked === true;
    const isBusinessReviewOrApproved = ['business_review', 'business_approved'].includes(reportDataStatus || '');
    return role && ['ADMIN', 'SUB_ADMIN', 'QA'].includes(role) && !isLocked && !isBusinessReviewOrApproved;
  }, [userRole, mergedData, reportDataStatus]);

  const statusInfo = useMemo(() => {
    switch (reportDataStatus) {
      case 'draft':
        return { label: 'Draft', color: 'text-slate-700 bg-slate-100 border-slate-300' };
      case 'qa_review':
        return { label: 'QA Review', color: 'text-amber-800 bg-amber-50 border-amber-300' };
      case 'admin_review':
        return { label: 'Admin Review', color: 'text-blue-800 bg-blue-50 border-blue-300' };
      case 'approved':
        return { label: 'Approved', color: 'text-emerald-800 bg-emerald-50 border-emerald-300' };
      case 'rejected':
        return { label: 'Rejected', color: 'text-red-800 bg-red-50 border-red-300' };
      case 'business_review':
        return { label: 'Business Review', color: 'text-blue-900 bg-white border-blue-200' };
      case 'business_requested_changes':
        return { label: 'Business Requested Changes', color: 'text-red-900 bg-red-50 border-red-300' };
      case 'changes_added':
        return { label: 'Changes Added (QA)', color: 'text-amber-900 bg-amber-50 border-amber-300' };
      case 'report_updated':
        return { label: 'Report Updated (Admin)', color: 'text-blue-900 bg-blue-50 border-blue-300' };
      case 'business_approved':
        return { label: 'Business Approved', color: 'text-emerald-900 bg-emerald-50 border-emerald-300' };
      default:
        return { label: reportDataStatus?.toUpperCase() || 'UNKNOWN', color: 'text-slate-800 bg-white border-slate-300' };
    }
  }, [reportDataStatus]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="flex h-screen overflow-hidden select-none">
        {/* Editor Section - Premium Glassy Redesign */}
        <div
          className="ctb-editor-pane flex flex-col h-full overflow-hidden bg-white/60 backdrop-blur-xl border-r border-gray-200 shadow-2xl relative z-10"
          style={{ width: editorWidth, minWidth: 0, maxWidth: '100%', transition: dragging.current ? 'none' : 'width 0.2s' }}
        >
          {/* Professional Compact Blue Header */}
          <div className="sticky top-0 z-30 w-full bg-blue-700 px-4 py-2 border-b border-blue-800/80 shadow-sm">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              {/* Left: Title + Status Badge */}
              <div className="flex items-center gap-2 shrink-0">
                <h2 className="text-white text-sm font-bold flex items-center gap-1.5 whitespace-nowrap">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  Edit Report
                </h2>
                {reportData && (
                  <span className={`px-2.5 py-0.5 text-[11px] font-bold rounded-full shadow-xs border whitespace-nowrap ${statusInfo.color}`}>
                    {statusInfo.label}
                  </span>
                )}
              </div>

              {/* Right: Template Selector & Actions */}
              <div className="flex items-center gap-1.5 flex-wrap shrink-0">
                {/* Template Selector */}
                <div className="flex items-center gap-1 bg-blue-800/70 border border-blue-600/50 rounded-lg px-2 py-1 shrink-0">
                  <span className="text-[10px] font-bold text-blue-200 uppercase tracking-wider whitespace-nowrap">Template:</span>
                  {canEditTemplate ? (
                    <select
                      value={mergedData?.template || 'template-1'}
                      onChange={(e) => handleDataChange('template', e.target.value)}
                      className="bg-transparent text-white text-xs font-semibold focus:outline-none cursor-pointer pr-0.5 whitespace-nowrap"
                      style={{ colorScheme: 'dark' }}
                    >
                      <option value="template-1" className="text-slate-900 bg-white font-semibold">Template 1</option>
                      <option value="template-2" className="text-slate-900 bg-white font-semibold">Template 2</option>
                    </select>
                  ) : (
                    <span className="text-xs font-semibold text-white whitespace-nowrap">
                      {mergedData?.template === 'template-2' ? 'Template 2' : 'Template 1'}
                    </span>
                  )}
                </div>

                {/* Edit Logs */}
                <button
                  type="button"
                  onClick={openChangeLog}
                  className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-white text-blue-700 border border-blue-200 hover:bg-blue-50 transition-all flex items-center gap-1 shadow-xs whitespace-nowrap shrink-0"
                  title="View Change Logs"
                >
                  <svg className="w-3.5 h-3.5 text-blue-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span>Edit Logs</span>
                </button>

                {/* Save Button */}
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saveLoading}
                  className={`px-3 py-1 rounded-lg text-xs font-bold bg-white text-blue-700 border border-blue-200 hover:bg-blue-50 transition-all flex items-center gap-1 shadow-xs whitespace-nowrap shrink-0 ${
                    saveLoading ? 'opacity-60 cursor-not-allowed' : ''
                  }`}
                  title="Save Report Changes"
                >
                  <svg className="w-3.5 h-3.5 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{saveLoading ? 'Saving...' : 'Save'}</span>
                </button>

                {/* Download LOA */}
                {props.onDownloadLOA && (
                  isLoaEnabled ? (
                    <button
                      type="button"
                      onClick={props.onDownloadLOA}
                      disabled={props.loaDownloading}
                      className={`px-2.5 py-1 rounded-lg text-xs font-bold bg-white text-blue-800 border border-blue-200 hover:bg-blue-50 transition-all flex items-center gap-1 shadow-xs whitespace-nowrap shrink-0 ${
                        props.loaDownloading ? 'opacity-60 cursor-not-allowed' : ''
                      }`}
                      title="Download CTB-branded Letter of Attestation (PDF)"
                    >
                      <svg className="w-3.5 h-3.5 text-blue-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>{props.loaDownloading ? 'Downloading...' : 'Download LOA'}</span>
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => props.setActiveSection('attestation_letter')}
                      className="px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-800/80 hover:bg-blue-800 text-blue-200 border border-blue-500/40 transition-all flex items-center gap-1.5 shadow-xs whitespace-nowrap shrink-0"
                      title="Letter of Attestation is currently disabled. Click to configure and enable."
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span>
                      <span>LOA: Off</span>
                    </button>
                  )
                )}

                {/* Approve */}
                {canApprove && (
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={approveLoading}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold bg-emerald-500 hover:bg-emerald-600 text-white border border-emerald-400 transition-all flex items-center gap-1 shadow-xs whitespace-nowrap shrink-0 ${
                      approveLoading ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                  >
                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{approveLoading ? 'Approving...' : 'Approve'}</span>
                  </button>
                )}

                {/* Reject */}
                {canReject && (
                  <button
                    type="button"
                    onClick={handleReject}
                    disabled={rejectLoading}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold bg-red-500 hover:bg-red-600 text-white border border-red-400 transition-all flex items-center gap-1 shadow-xs whitespace-nowrap shrink-0 ${
                      rejectLoading ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                  >
                    <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>{rejectLoading ? 'Rejecting...' : 'Reject'}</span>
                  </button>
                )}

                {/* Upload to Vanta */}
                {props.showUploadToVanta && (
                  <button
                    type="button"
                    onClick={props.onUploadToVanta}
                    disabled={props.vantaUploadLoading}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold bg-white text-indigo-700 border border-indigo-200 hover:bg-indigo-50 transition-all flex items-center gap-1 shadow-xs whitespace-nowrap shrink-0 ${
                      props.vantaUploadLoading ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                    title="Upload the exact preview PDF to Vanta"
                  >
                    <svg className="w-3.5 h-3.5 text-indigo-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01.88-7.903A5 5 0 0117 9h1a4 4 0 010 8H7zm5-9v8m0 0l-3-3m3 3l3-3" />
                    </svg>
                    <span>{props.vantaUploadLoading ? `Uploading... ${Math.max(0, Math.min(100, props.vantaUploadProgress || 0))}%` : 'Upload to Vanta'}</span>
                  </button>
                )}
              </div>
            </div>
          </div>
          {/* Navigation */}
          <div className="border-b border-transparent bg-transparent">
            <EditorNavigation
              activeSection={activeSection}
              onSectionChange={setActiveSection}
              reportData={currentData}
              userRole={userRole}
            />
          </div>
          {/* Editor Content */}
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-6 bg-transparent">
            <div className="max-w-3xl mx-auto">
              <SectionRenderer
                activeSection={activeSection}
                currentData={currentData}
                sectionData={props.sectionData}
                handleInputChange={handleInputChange}
                handleHtmlChange={handleHtmlChange}
                handleDataChange={handleDataChange}
                handleFindingChange={handleFindingChange}
                handleRemoveFinding={handleRemoveFinding}
                handleAddFinding={handleAddFinding}
                handleTargetDetailsChange={handleTargetDetailsChange}
                handleAddTarget={handleAddTarget}
                handleRemoveTarget={handleRemoveTarget}
                handleTableChange={handleTableChange}
                handleKeyFindingChange={handleKeyFindingChange}
                handleAddKeyFinding={handleAddKeyFinding}
                handleRemoveKeyFinding={handleRemoveKeyFinding}
                handleRecommendationsChange={handleRecommendationsChange}
              />
            </div>
          </div>
        </div>
        {/* Draggable Divider */}
        <div
          className="group flex-shrink-0 w-2 cursor-col-resize relative z-20"
          style={{ userSelect: 'none' }}
          onMouseDown={() => { dragging.current = true; }}
        >
          <div className="absolute left-1/2 top-0 -translate-x-1/2 h-full w-1 rounded bg-gradient-to-b from-blue-200 via-blue-400 to-blue-200 shadow-lg group-hover:from-blue-300 group-hover:to-blue-500 group-hover:shadow-xl transition-all duration-200" />
        </div>
        {/* Preview Section */}
        <div className="flex-1 h-full overflow-hidden bg-gradient-to-br from-blue-50/80 to-blue-100/60">
          <div className="h-full flex flex-col">
            <div className="flex-1 overflow-y-auto">
              <PreviewSection
                ref={props.previewRef}
                reportData={mergedData}
                initialPreviewMode={['full', 'technical', 'executive'].includes(previewMode) ? previewMode as 'full' | 'technical' | 'executive' : undefined}
                onPreviewModeChange={setPreviewMode}
              />
            </div>
          </div>
        </div>
      </div>
      <ChangeLogSidebar
        isOpen={isChangeLogOpen}
        onClose={closeChangeLog}
        changeLog={changeLog}
        loading={changeLogLoading}
      />
      {children}
    </div>
  );
};

export default EditorLayout; 